// utils/hooks/useIdleTimer.js

import { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '@/redux/auth/actions';
import { selectAuth } from './selectors';

const CHECK_INTERVAL = 10000; // 10 seconds

const useIdleTimer = (isLoggedIn) => {
  const dispatch = useDispatch();
  const lastActivityRef = useRef(Date.now());
  const intervalRef = useRef(null);

  // Get current user from Redux store
  const currentUser = useSelector(selectAuth);

  // Determine timeout based on user role
  const getIdleTimeout = useCallback(() => {
    if (!currentUser || !currentUser.role) return 15 * 60 * 1000; // Default 15 minutes

    const roleName = currentUser.role.name;
    const privilegedRoles = ['CTO', 'Strategy Analyst', 'TO Finance', 'Admin'];

    if (privilegedRoles.includes(roleName)) {
      return 30 * 60 * 1000; // 60 minutes
    } else {
      return 15 * 60 * 1000; // 15 minutes
    }
  }, [currentUser]);

  const logoutUser = useCallback(() => {
    dispatch(logout());
  }, [dispatch]);

  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const checkIdle = useCallback(() => {
    const idleTimeout = getIdleTimeout();
    const idleTime = Date.now() - lastActivityRef.current;

    if (idleTime >= idleTimeout) {
      logoutUser();
    }
  }, [logoutUser, getIdleTimeout]);

  useEffect(() => {
    if (!isLoggedIn) return;

    lastActivityRef.current = Date.now();

    intervalRef.current = setInterval(checkIdle, CHECK_INTERVAL);

    const throttledActivity = throttle(updateActivity, 1000);

    const events = [
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'click',
      'focus'
    ];

    events.forEach(e =>
      window.addEventListener(e, throttledActivity, true)
    );

    const onVisibilityChange = () => {
      if (!document.hidden) updateActivity();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalRef.current);
      events.forEach(e =>
        window.removeEventListener(e, throttledActivity, true)
      );
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isLoggedIn, checkIdle, updateActivity]);

  // Log the timeout for debugging (optional)
  useEffect(() => {
    if (isLoggedIn && currentUser) {
      const timeout = getIdleTimeout() / (60 * 1000);
      console.log(`Idle timeout set to ${timeout} minutes for role: ${currentUser.role?.name}`);
    }
  }, [isLoggedIn, currentUser, getIdleTimeout]);
};

function throttle(fn, delay) {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  };
}

export default useIdleTimer;